import { fr } from "@codegouvfr/react-dsfr";
import Alert from "@codegouvfr/react-dsfr/Alert";
import Input from "@codegouvfr/react-dsfr/Input";
import { createModal } from "@codegouvfr/react-dsfr/Modal";
import { Select } from "@codegouvfr/react-dsfr/Select";
import { useEffect, useRef, useState } from "react";
import { tss } from "tss-react/dsfr";
import { CheckTexts } from "../utils/CheckTexts";
import type { PositionData } from "../../utils/buildPositionData";
import { captureMapImage } from "../../utils/captureMapImage";

export const modal = createModal({
	id: "diagnostic-receive-by-mail-modal",
	isOpenedByDefault: false,
});

const isValidEmail = (email: string) =>
	/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const submitAlertMessages: Record<
	"success" | "error" | "rate-limit",
	{ severity: "success" | "error"; title: string; description: string }
> = {
	success: {
		severity: "success",
		title: "Demande enregistrée avec succès",
		description: "Vous recevrez votre diagnostic par email prochainement.",
	},
	"rate-limit": {
		severity: "error",
		title: "Trop de demandes",
		description:
			"Vous avez effectué trop de demandes. Veuillez patienter quelques instants avant de réessayer.",
	},
	error: {
		severity: "error",
		title: "Une erreur est survenue",
		description: "Impossible d'enregistrer votre email. Veuillez réessayer.",
	},
};

const selectOptions = [
	{ value: "architecte", label: "Architecte" },
	{ value: "charge-de-mission-bruit", label: "Chargé de mission bruit" },
	{ value: "instructeur", label: "Instructeur ADS" },
	{ value: "particulier", label: "Particulier" },
	{ value: "promoteur", label: "Promoteur" },
	{ value: "service-amenagement", label: "Service aménagement" },
	{ value: "autre", label: "Autre" },
];

export type DiagnosticEmailSummary = {
	score: number;
	maxDbLden?: number;
	flags: {
		isMultiExposedSources: boolean;
		isPriorityZone: boolean;
		hasClassificationWarning: boolean;
		hasNoisemapWarning: boolean;
	};
	regulation?: {
		peb: { exposed: boolean; zone: string | null };
		soundClassification: {
			exposed: boolean;
			rows: {
				type: string;
				name: string;
				category: number;
				minDistance: number;
				maxDistance: number;
			}[];
		};
	};
	isolation?: {
		min: number | null;
		max: number | null;
		hasPeb: boolean;
		hasCls: boolean;
	};
	plu?: {
		zones: {
			label: string;
			content: string;
			source: string;
			reference: string;
		}[];
	};
	noiseMap?: {
		rows: {
			type: string;
			producer: string;
			name: string;
			dayLevel: string;
			nightLevel: string;
		}[];
	};
	noiseSources?: {
		name: string;
		slug: string;
		count: number;
	}[];
	position?: PositionData;
	mapImage?: string;
};

export default function DiagnosticEmailForm({
	onSuccess,
	onClose,
	parcelNumber,
	summary,
}: {
	onSuccess?: (email: string) => void;
	onClose?: () => void;
	parcelNumber?: string;
	summary?: DiagnosticEmailSummary;
}) {
	const { cx, classes } = useStyles();

	useEffect(() => {
		const modalElement = document.getElementById(modal.id);
		if (!modalElement) return;
		const handleConceal = () => onClose?.();
		modalElement.addEventListener("dsfr.conceal", handleConceal);
		return () =>
			modalElement.removeEventListener("dsfr.conceal", handleConceal);
	}, [onClose]);

	const formRef = useRef<HTMLFormElement>(null);
	const [value, setValue] = useState("");
	const [email, setEmail] = useState("");
	const [profileError, setProfileError] = useState(false);
	const [emailError, setEmailError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<
		"success" | "error" | "rate-limit" | null
	>(null);

	const submitEmail = async () => {
		const subscribeResponse = await fetch(
			`${process.env.REACT_APP_CMS_URL}/api/email/subscribe`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email, profile: value }),
			},
		);
		if (!subscribeResponse.ok) {
			if (subscribeResponse.status === 429) {
				throw new Error("rate-limit");
			}
			throw new Error(
				`Erreur lors de l'enregistrement : ${subscribeResponse.status}`,
			);
		}

		// Capture the current map view (satellite + parcel outline) for the PDF.
		const summaryWithMap = summary
			? { ...summary, mapImage: captureMapImage() ?? undefined }
			: summary;

		const mailResponse = await fetch(
			`${process.env.REACT_APP_CMS_URL}/api/email/send`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ to: email, link: window.location.href, parcelNumber, summary: summaryWithMap }),
			},
		);
		if (!mailResponse.ok) {
			if (mailResponse.status === 429) {
				throw new Error("rate-limit");
			}
			throw new Error(
				`Erreur lors de l'envoi de l'email : ${mailResponse.status}`,
			);
		}
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const hasProfileError = !value;
		const hasEmailError = !email || !isValidEmail(email);
		setProfileError(hasProfileError);
		setEmailError(hasEmailError);
		if (hasProfileError || hasEmailError) return;

		setIsSubmitting(true);
		setSubmitStatus(null);
		submitEmail()
			.then(() => {
				modal.close();
				onSuccess?.(email);
			})
			.catch((err) => {
				console.error(err);
				setSubmitStatus(err.message === "rate-limit" ? "rate-limit" : "error");
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};



	return (
		<modal.Component
			title="Recevoir le diagnostic par email"
			size="large"
			buttons={[
				{
					children: isSubmitting ? "Envoi en cours..." : "Valider",
					priority: "primary",
					iconId: "ri-check-line",
					disabled: isSubmitting,
					doClosesModal: false,
					onClick: () => formRef.current?.requestSubmit(),
				},
			]}
		>
			{submitStatus && (
				<Alert
					closable
					{...submitAlertMessages[submitStatus]}
					onClose={() => setSubmitStatus(null)}
					className={fr.cx("fr-mb-4v")}
				/>
			)}
			<div className={cx(classes.container, "fr-grid-row")}>
				<div className={cx(classes.tileTitle, "fr-col-2")}>
					<img src="/images/document-download.svg" alt="" />
				</div>
				<div className={cx(classes.tileContent, "fr-col-10")}>
					{<CheckTexts />}
				</div>
			</div>
			<form ref={formRef} onSubmit={handleSubmit}>
				<div className={classes.formInputContainer}>
					<Select
						label="Mon Profil"
						state={profileError ? "error" : "default"}
						stateRelatedMessage={
							profileError ? "Veuillez sélectionner un profil" : undefined
						}
						nativeSelectProps={{
							onChange: (event) => {
								setValue(event.target.value);
								setProfileError(false);
							},
							value,
						}}
					>
						<option value="" disabled hidden>
							Selectionnez un profil
						</option>
						{selectOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
					<Input
						label="Adresse email"
						state={emailError ? "error" : "info"}
						stateRelatedMessage={
							emailError ? (
								!email ? (
									"Veuillez saisir une adresse email"
								) : (
									"Veuillez saisir une adresse email valide (exemple: exemple@exemple.com)"
								)
							) : (
								<span>
									{
										"Votre adresse email sera utilisée uniquement pour vous transmettre votre diagnostic et vous accompagner avec des conseils adaptés à votre projet. "
									}
									<a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Voir nos engagements</a>
								</span>
							)
						}
						nativeInputProps={{
							value: email,
							onChange: (event) => {
								setEmail(event.target.value);
								setEmailError(false);
							},
						}}
					/>
				</div>
			</form>
		</modal.Component>
	);
}

const useStyles = tss.create(() => ({
	container: {
		marginTop: fr.spacing("4v"),
		border: `1px solid ${fr.colors.decisions.border.default.blueFrance.default}`,
	},
	tileTitle: {
		borderRight: `1px solid ${fr.colors.decisions.border.default.blueFrance.default}`,
		background: fr.colors.decisions.background.alt.blueFrance.default,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: fr.spacing("4v"),
	},
	tileContent: {
		padding: fr.spacing("4v"),
		marginTop: fr.spacing("2v"),
		p: {
			margin: `0 0 0 ${fr.spacing("1v")} `,
		},
	},
	formInputContainer: {
		marginTop: fr.spacing("4v"),
	},
}));
